// src/api/auth.ts
import { API_BASE } from "../utils/api";
import { clearAll as clearStore } from "../utils/store";

// M5-STORE1 (15.09.2026): единая точка очистки офлайн-хранилища при выходе.
// logout() и clearSession() расходятся в охвате (сетевой/локальный выход) —
// обеим нужен один и тот же вызов, не два реализации. Отказ хранилища не
// должен сорвать выход — clearStore() сама не бросает (см. utils/store.ts),
// catch здесь — дополнительная защита на случай будущих изменений контракта.
function clearOfflineStore() {
  void clearStore().catch((e) => {
    console.error("[store] clearAll on sign-out failed:", e instanceof Error ? e.message : String(e));
  });
}

// Оптимистичный локальный флаг входа (Задание 3B, cookie-сессия). Переживает
// перезагрузку страницы — без него приложение не открывалось бы офлайн, т.к.
// сетевой tryRefresh() на старте больше не выполняется. Источник истины —
// сервер: 401 на гейте гасит флаг (см. fetchWithAuth/clearSession), сетевая
// ошибка (офлайн) — нет.
const AUTHED_FLAG_KEY = "mira_authed";

function setAuthedFlag(v: boolean) {
  try {
    if (v) localStorage.setItem(AUTHED_FLAG_KEY, "1");
    else localStorage.removeItem(AUTHED_FLAG_KEY);
  } catch {}
}

function getAuthedFlag(): boolean {
  try {
    return localStorage.getItem(AUTHED_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

// Задание 3E (02.08.2026): CSRF-токен переехал из cookie в localStorage —
// __Host-ms_csrf_* был привязан к api.24mira.ru и принципиально невидим из
// document.cookie на странице 24mira.ru (разные механизмы: отправка cookie —
// по домену запроса, видимость в document.cookie — по домену документа).
// localStorage может быть недоступен (приватный режим, квота) — try/catch
// обязателен на каждом обращении, недоступность не должна ронять приложение.
const CSRF_TOKEN_KEY = "mira_csrf";

function setCsrfToken(v: string | null | undefined) {
  try {
    if (v) localStorage.setItem(CSRF_TOKEN_KEY, v);
    else localStorage.removeItem(CSRF_TOKEN_KEY);
  } catch {}
}

export const auth = {
  /** вызывать один раз при старте приложения. Сессия — скользящая cookie
   * (idle-TTL продлевается сервером через last_seen_at), сетевой обмен на
   * access_token при каждом старте (tryRefresh) больше не нужен — статус
   * входа читается локально из AUTHED_FLAG_KEY, без сети (Задание 3B). */
  async init() {},

  /** httpOnly refresh cookie "rt". Клиент PWA сам больше не вызывает (см.
   * init/fetchWithAuth) — эндпоинт /auth/bff/refresh не трогается, им
   * пользуется партнёрка. Оставлена для совместимости, токена в ответе
   * больше не читает (Задание 4 — access_token в системе не существует). */
  async tryRefresh() {
    try {
      const res = await fetch(`${API_BASE}/auth/bff/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    }
  },

  async logout() {
    try {
      await fetch(`${API_BASE}/auth/bff/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    setAuthedFlag(false);
    setCsrfToken(null);
    window.dispatchEvent(new Event("auth:changed"));
    try { localStorage.removeItem("clientId"); } catch {}
    clearOfflineStore();
  },

  /** Сервер подтвердил недействительность сессии (401 на гейте, см.
   * fetchWithAuth) — гасим локальный флаг без сетевого /auth/bff/logout.
   * Сетевая ошибка (офлайн) сюда попадать не должна. */
  clearSession() {
    setAuthedFlag(false);
    setCsrfToken(null);
    window.dispatchEvent(new Event("auth:changed"));
    clearOfflineStore();
  },

  isAuthenticated() {
    return getAuthedFlag();
  },

  /** Отметить успешный вход (OTP verify-code). Задание 4: access_token
   * в системе больше не существует — csrf передаётся явно из ответа
   * verify-code вызывающей стороной (OtpAuthScreen.handleAuthSuccess),
   * а не через побочный эффект getCurrentUser(). */
  completeLogin(csrf?: string | null) {
    setAuthedFlag(true);
    if (csrf !== undefined) setCsrfToken(csrf);
    window.dispatchEvent(new Event("auth:changed"));
  },

  /** Задание 3E: записать/обновить CSRF-токен в localStorage — вызывается из
   * utils/users.tsx после каждого успешного /auth/bff/me (в т.ч. точка
   * восстановления, если localStorage очистился, а сессия ещё жива). */
  setCsrfToken,
};
